import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { cn } from '@/lib/utils';

const alertVariants = cva(
  'relative grid w-full grid-cols-[0_1fr] items-start gap-y-1 rounded-lg border px-4 py-3.5 text-sm has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] has-[>svg]:gap-x-3 [&>svg]:size-4 [&>svg]:translate-y-0.5',
  {
    variants: {
      tone: {
        default: 'border-border bg-card text-card-foreground',
        info: 'border-transparent bg-info-bg text-info-fg',
        warn: 'border-transparent bg-warn-bg text-warn-fg',
        err: 'border-transparent bg-err-bg text-err-fg',
      },
    },
    defaultVariants: { tone: 'default' },
  },
);

export type AlertProps = ComponentProps<'div'> & VariantProps<typeof alertVariants>;

export function Alert({ className, tone, ...props }: AlertProps) {
  return (
    <div
      data-slot="alert"
      // Announced by default, because that is what an alert is for. A caller
      // whose message is context rather than news — a footnote inside a panel
      // — passes `role="note"` and wins, since props spread last.
      role="alert"
      className={cn(alertVariants({ tone }), className)}
      {...props}
    />
  );
}

export function AlertTitle({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-title"
      className={cn('col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight', className)}
      {...props}
    />
  );
}

export function AlertDescription({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        'col-start-2 grid justify-items-start gap-1 text-sm leading-relaxed opacity-90',
        className,
      )}
      {...props}
    />
  );
}
